// src/components/achievements/AchievementCard.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Lock } from 'lucide-react';

interface AchievementCardProps {
  title: string;
  description: string;
  icon: string;
  unlocked: boolean;
  progress?: number;
  requirement?: number;
}

export const AchievementCard: React.FC<AchievementCardProps> = ({
  title,
  description,
  icon,
  unlocked,
  progress = 0,
  requirement = 100,
}) => {
  const progressPercentage = Math.min((progress / requirement) * 100, 100);

  return (
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      whileHover={{ scale: unlocked ? 1.05 : 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <Card
        className={`relative overflow-hidden ${
          unlocked
            ? 'border-2 border-yellow-500/50 bg-gradient-to-br from-yellow-500/20 to-amber-500/20'
            : 'border-2 border-muted bg-muted/50 opacity-60'
        }`}
      >
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="relative">
              <div
                className={`text-5xl ${
                  unlocked ? 'animate-float' : 'grayscale opacity-40'
                }`}
              >
                {icon}
              </div>
              {!unlocked && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Lock className="w-6 h-6 text-muted-foreground" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-bold text-lg">{title}</h3>
                {unlocked && (
                  <Badge variant="default" className="bg-yellow-500">
                    Unlocked
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground mb-3">{description}</p>
              {!unlocked && (
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Progress</span>
                    <span>
                      {progress} / {requirement}
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
                      initial={{ width: 0 }}
                      animate={{ width: `${progressPercentage}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

